import {
  StageEngagementFragment,
  StageGetActiveEngagementDocument,
  StageGetActiveEngagementQuery,
  StageGetEventDocument,
  StageGetEventQuery,
  useOnActiveEngagementChangedSubscription,
  useOnEngagementChangedSubscription,
  useOnMockEngagementChangedSubscription,
  useStageGetEventQuery,
} from '@/generated/graphql';
import { createLogger } from '@/utils/createLogger';
import { useSearchParams } from 'react-router-dom';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';

const logger = createLogger('useStageEvent');

export function useStageEvent(id: string) {
  const [searchParams] = useSearchParams();
  const engagementIdOverride = searchParams.get('engagementId');

  const { data, loading, error } = useStageGetEventQuery({
    variables: {
      id,
    },
    onError: (error) => {
      logger.error('Error fetching event', error);
    },
    onCompleted: () => {
      logger.debug('Event fetched');
    },
  });

  // this will update the cache with the new active engagement
  useOnActiveEngagementChangedSubscription({
    variables: { eventId: id },
    onData: ({ client, data }) => {
      const nextActiveEngagement =
        data?.data?.activeEngagementChanged?.activeEngagement;

      logger.debug(
        `Active engagement switched for event ${id} to ${nextActiveEngagement ? nextActiveEngagement.id : 'none'}`,
      );

      // Update the Apollo cache with the new active engagement
      client.cache.updateQuery<StageGetEventQuery>(
        {
          query: StageGetEventDocument,
          variables: { id },
        },
        (cachedData) => {
          if (!cachedData?.event) return cachedData;

          return {
            event: {
              ...cachedData.event,
              activeEngagement: nextActiveEngagement || null,
            },
          };
        },
      );

      // Also update the StageGetActiveEngagement query
      client.cache.updateQuery<StageGetActiveEngagementQuery>(
        {
          query: StageGetActiveEngagementDocument,
          variables: { eventId: id },
        },
        (cachedData) => {
          return {
            activeEventEngagement:
              cachedData?.activeEventEngagement || nextActiveEngagement
                ? ({
                    ...cachedData?.activeEventEngagement,
                    ...nextActiveEngagement,
                  } as StageEngagementFragment)
                : null,
          };
        },
      );
    },
  });

  const event = data?.event;
  const engagement = event?.activeEngagement;

  const skip =
    (!event || !engagement || !engagement.id) && !engagementIdOverride;
  const mockRealtime = useMockRealtimeChannel();
  logger.info(`calling useOnEngagementChangedSubscription. skip: ${skip}`, {
    engagementIdOverride,
    mockRealtime,
  });

  useOnEngagementChangedSubscription({
    skip,
    variables: {
      engagementId: engagementIdOverride || engagement?.id || '',
    },
    onData: ({ data, client }) => {
      logger.debug('Active engagement modified, updating cache', data);
      const changes = data?.data?.engagementChanged?.engagement;
      if (changes) {
        client.cache.updateQuery<StageGetEventQuery>(
          {
            query: StageGetEventDocument,
            variables: { id: event?.id },
          },
          (cachedData) => {
            if (!cachedData?.event) return cachedData;

            return {
              event: {
                ...cachedData.event,
                activeEngagement: {
                  ...cachedData.event.activeEngagement!,
                  ...changes,
                },
              },
            } as StageGetEventQuery;
          },
        );
      }
    },
  });
  useOnMockEngagementChangedSubscription({
    skip: skip || !mockRealtime,
    variables: {
      engagementId: engagementIdOverride || engagement?.id || '',
    },
    onData: ({ data, client }) => {
      logger.debug('Mock engagement modified, updating cache', data);
      const changes = data?.data?.mockEngagementChanged?.engagement;
      if (changes) {
        client.cache.updateQuery<StageGetEventQuery>(
          {
            query: StageGetEventDocument,
            variables: { id: event?.id },
          },
          (cachedData) => {
            if (!cachedData?.event) return cachedData;

            return {
              event: {
                ...cachedData.event,
                activeEngagement: {
                  ...cachedData.event.activeEngagement!,
                  ...changes,
                },
              },
            } as StageGetEventQuery;
          },
        );
      }
    },
  });

  return {
    data,
    loading,
    error,
  };
}
